import MatchingChip from 'components/Chips/MatchingChip';
import MentorshipStatusChip from 'components/Chips/MentorshipStatusChip';
import CopyText from 'components/CopyText';
import { ActionDrawer } from 'components/Drawer';
import CheckboxList from 'components/Drawer/CheckboxList';
import EllipsisText from 'components/EllipsisText';
import getFilters from 'components/Filters';
import LabelledCheckbox from 'components/form/Inputs/LabelledCheckbox';
import SettingsIcon from 'components/icons/Settings';
import List from 'components/list';
import SettingsContainer from 'components/list/SettingsContainer';
import { BoldTd, Td } from 'components/list/Td';
import { Th } from 'components/list/Th';
import { getColumnNumber } from 'components/list/utils';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import MENTORSHIP_STATUS from 'enums/MentorshipStatus';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  setActiveColumns,
  setPage,
  setSearch,
  setTags,
} from 'store/actions/admin/mentorLists';
import { StoreState } from 'store/reducers';
import useSWR from 'swr';
import Tags from 'types/Tags';
import { Action } from 'types/typeguards/reducerActions';
import User from 'types/User';
import { ongoingTags } from 'utils/mentorshipTags';
import { getLocaleDate } from 'utils/time';
import { MentorEditContext } from '.';

export const columnNames = [
  'matching',
  'firstName',
  'lastName',
  'start',
  'email',
  'region',
  'postalCode',
  'status',
] as const;

export type ColumnNames = typeof columnNames[number];
export type Columns = {
  [key in ColumnNames]: boolean;
};
export const initialActiveColumns: Columns = {
  matching: true,
  firstName: true,
  lastName: true,
  start: true,
  email: true,
  region: true,
  postalCode: false,
  status: true,
};

export const tagColumnNames = ['status'] as const;
export type TagColumnNames = typeof tagColumnNames[number];

type RetrievedData = {
  mentees: User[] | undefined;
  count: number | undefined;
  pages: number | undefined;
};

type Props = {
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (search: string) => void;
  tags: Tags<TagColumnNames>;
  setTags: (tag: Tags<TagColumnNames>) => void;
  activeColumns: Columns;
  setActiveColumns: (activeColumns: Columns) => void;
};

type ContextType = {
  mentees: User[] | undefined;
  pages: number | undefined;
  loading: boolean;
} & Props;
const OngoingContext = React.createContext({} as ContextType);
const { Provider } = OngoingContext;

const useCustomSWR = (
  id: number | undefined,
  take: number,
  page: number,
  search: string,
  tags: Tags<TagColumnNames>,
) => {
  const { data, error } = useSWR(
    `/mentorship/mentor/${id}/ongoing?take=${take}&page=${page}\
		${search ? `&search=${search}` : ''}\
		${
      tags.status[0]
        ? tags.status.map((tag) => `&mentorshipTags=${tag}`).join('')
        : ''
    }`,
  );
  if (!id) {
    return { data: undefined };
  }
  return { data, error };
};

const Ongoing: FC<Props> = ({
  children,
  page,
  setPage,
  search,
  setSearch,
  tags,
  setTags,
  activeColumns,
  setActiveColumns,
}) => {
  const { mentor } = useContext(MentorEditContext);

  const { data, error }: { data?: RetrievedData; error?: unknown } =
    useCustomSWR(mentor?.id, 6, page, search, tags);
  const [retrievedData, setRetrievedData] = useState<RetrievedData>({
    mentees: undefined,
    pages: undefined,
    count: undefined,
  });
  useEffect(() => {
    if (data) {
      setRetrievedData(data);
    }
  }, [data]);
  // Reset page to 0 if the stored page is out of range
  useEffect(() => {
    if (data?.pages && data?.pages <= page) {
      setPage(0);
    }
  }, [data, page, setPage]);

  const value = useMemo(
    () => ({
      mentees: retrievedData.mentees,
      pages: retrievedData.pages,
      page,
      setPage,
      search,
      setSearch,
      tags,
      setTags,
      activeColumns,
      setActiveColumns,
      loading: !data,
    }),
    [
      page,
      setPage,
      search,
      setSearch,
      tags,
      setTags,
      activeColumns,
      setActiveColumns,
      data,
      retrievedData,
    ],
  );

  return error ? (
    <p>L'utilisateur n'a pas rempli son formulaire correctement.</p>
  ) : retrievedData.mentees ? (
    <Provider value={value}>
      <section>{children}</section>
    </Provider>
  ) : (
    <LoaderContainer height="auto">
      <DefaultLoader />
    </LoaderContainer>
  );
};

const Filters = getFilters<TagColumnNames>();
const OngoingFilter: FC = () => {
  const { search, setSearch, tags, setTags } = useContext(OngoingContext);

  return (
    <Filters
      tagList={[
        {
          columnName: 'status',
          columnTitle: 'Statut du mentorat',
          tags: ongoingTags.map((tag) => {
            return {
              name: tag,
              label: MENTORSHIP_STATUS[tag],
              labelComponent: <MentorshipStatusChip status={tag} />,
            };
          }),
        },
      ]}
      state={{ tags, search, take: 30 }}
      setTags={setTags}
      setSearch={setSearch}
    />
  );
};

const OngoingList: FC = () => {
  const {
    mentees,
    pages,
    page,
    setPage,
    loading,
    activeColumns,
    setActiveColumns,
  } = useContext(OngoingContext);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState(activeColumns);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSelectedColumns({
      ...selectedColumns,
      [e.target.name]: e.target.checked,
    });
  };

  const columnNumber = useMemo(() => {
    return getColumnNumber(activeColumns, false);
  }, [activeColumns]);

  return (
    <>
      <ActionDrawer
        open={settingsOpen}
        close={() => setSettingsOpen(false)}
        title="Colonnes mentorés"
        subtitle="Activer les colonnes que vous souhaitez"
        cancel={() => {
          setSelectedColumns(activeColumns);
        }}
        apply={() => {
          setActiveColumns(selectedColumns);
        }}
      >
        <CheckboxList>
          <LabelledCheckbox
            label="Matching"
            checked={selectedColumns.matching}
            name="matching"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Début"
            checked={selectedColumns.start}
            name="start"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Email"
            checked={selectedColumns.email}
            name="email"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Région"
            checked={selectedColumns.region}
            name="region"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Code postal"
            checked={selectedColumns.postalCode}
            name="postalCode"
            onChange={handleChange}
          />
          <LabelledCheckbox
            label="Statut du mentorat"
            checked={selectedColumns.status}
            name="status"
            onChange={handleChange}
          />
        </CheckboxList>
      </ActionDrawer>
      <List
        activeColumns={activeColumns}
        columnNumber={columnNumber}
        isEmpty={mentees?.length === 0}
        emptyText="Aucun mentoré n'est disponible pour cette recherche"
        pages={pages}
        page={page}
        setPage={setPage}
        loading={loading}
      >
        <Th name="matching">Matching</Th>
        <Th name="firstName">Prénom</Th>
        <Th name="lastName">Nom</Th>
        <Th name="start">Début</Th>
        <Th name="email">Email</Th>
        <Th name="region">Région</Th>
        <Th name="postalCode">Code postal</Th>
        <Th name="status">Statut du mentorat</Th>
        <Th name="settings">
          <SettingsContainer onClick={() => setSettingsOpen(true)}>
            <SettingsIcon />
          </SettingsContainer>{' '}
        </Th>
        {mentees?.map((mentee, idx) => (
          <React.Fragment key={idx}>
            <Td name="matching">
              {mentee.MenteeMatching && mentee.MenteeMatching[0] && (
                <MatchingChip value={mentee.MenteeMatching[0].value} />
              )}{' '}
            </Td>
            <BoldTd name="firstName">
              <Link to={`/admin/mentoring/mentees/${mentee.id}/mentoring/`}>
                <EllipsisText text={mentee.firstName || ''} />
              </Link>
            </BoldTd>
            <BoldTd name="lastName">
              <Link to={`/admin/mentoring/mentees/${mentee.id}/mentoring/`}>
                <EllipsisText text={mentee.lastName || ''} />
              </Link>
            </BoldTd>
            <Td name="start">
              {mentee.MenteeMentorship &&
                mentee.MenteeMentorship[0].createdAt &&
                getLocaleDate(mentee.MenteeMentorship[0].createdAt)}
            </Td>
            <Td name="email">
              <EllipsisText text={mentee.email || ''} />
              <CopyText text="" copiedText={mentee.email || ''} />
            </Td>
            <Td name="region">
              <EllipsisText text={mentee.region || ''} />
            </Td>
            <Td name="postalCode">{mentee.postalCode}</Td>
            <Td name="status">
              <MentorshipStatusChip
                status={
                  (mentee.MenteeMentorship &&
                    mentee.MenteeMentorship[0].status) ||
                  'FINISHED_DATE'
                }
              />
            </Td>
            <Td name="settings"> </Td>
          </React.Fragment>
        ))}
      </List>
    </>
  );
};

const Usage: FC<Props> = (props) => {
  return (
    <Ongoing {...props}>
      <OngoingFilter />
      <OngoingList />
    </Ongoing>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    page: state.admin.mentorLists.ongoing.page,
    search: state.admin.mentorLists.ongoing.search,
    tags: state.admin.mentorLists.ongoing.tags,
    activeColumns: state.admin.mentorLists.ongoing.activeColumns,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setPage: (page: number) => dispatch(setPage('ongoing', page)),
    setSearch: (search: string) => dispatch(setSearch('ongoing', search)),
    setTags: (tags: Tags<TagColumnNames>) => dispatch(setTags('ongoing', tags)),
    setActiveColumns: (activeColumns: Columns) =>
      dispatch(setActiveColumns<Columns>('ongoing', activeColumns)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Usage);
